// Environment detail (design/isomorphic environment-detail): status and the stop, start
// or retry action, a summary band, then limits, workspaces, network and egress.
import { ArrowsClockwise, Play, Stop } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useParams } from "react-router";
import { Button } from "rizzui";

import { VpnLink } from "../../vpn/VpnLink";
import { ShellLink } from "./ShellLink";

import type { Environment } from "../../api/client";
import { orgsQuery, runsQuery, workflowsQuery } from "../../api/queries";
import { messages, useT } from "../../i18n";
import { toast } from "../../layout/toast";
import { day } from "../../pages/format";
import { cardClass, Field } from "../../ui/Card";
import { cn } from "../../ui/cn";
import { PageHeader } from "../../ui/PageHeader";
import { ErrorState, Loading } from "../../ui/states";
import { statusLabel } from "../../ui/tones";
import { environmentAction, type EnvironmentAction, environmentQuery } from "../api";
import { instanceName, liveRuns } from "./derive";
import { EgressCard, LimitsCard, NetworkCard, WorkspacesCard } from "./EnvironmentCards";
import { EnvironmentStatus } from "./marks";
import { detailCrumbs } from "../../layout/nav";

const ACTION: Partial<Record<string, { action: EnvironmentAction; icon: ReactNode; danger?: boolean }>> = {
  ready: { action: "stop", icon: <Stop weight="bold" className="h-4 w-4" />, danger: true },
  stopped: { action: "start", icon: <Play weight="bold" className="h-4 w-4" /> },
  failed: { action: "retry", icon: <ArrowsClockwise weight="bold" className="h-4 w-4" /> },
};

function ActionButton({ env }: { env: Environment }) {
  const t = useT().workloads.environment;
  const queryClient = useQueryClient();
  const next = ACTION[env.status];
  const mutation = useMutation({
    mutationFn: (action: EnvironmentAction) => environmentAction(env.id, action),
    onSuccess: async (updated) => {
      await queryClient.invalidateQueries({ queryKey: ["environments"] });
      toast(messages().workloads.environment.changed(statusLabel(updated.status)));
    },
    onError: (err) => toast(err.message, "warn"),
  });
  if (!next) return null;
  return (
    <Button variant="outline" color={next.danger ? "danger" : undefined} className="gap-1.5" disabled={mutation.isPending} onClick={() => mutation.mutate(next.action)}>
      {next.icon} {t.actions[next.action]}
    </Button>
  );
}

export default function EnvironmentDetailPage() {
  const { workloads } = useT();
  const t = workloads.environment;
  const { envId = "" } = useParams();
  const query = useQuery(environmentQuery(envId));
  const orgs = useQuery(orgsQuery());
  const workflows = useQuery(workflowsQuery());
  const runs = useQuery(runsQuery());

  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorState title={t.couldNotLoad} message={query.error.message} />;
  const env = query.data;
  const org = orgs.data?.find((o) => o.id === env.org_id);
  const live = liveRuns(env, workflows.data ?? [], runs.data ?? []);

  return (
    <>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {org?.name ?? "—"} / {env.name} <EnvironmentStatus status={env.status} />
          </span>
        }
        breadcrumb={detailCrumbs("environments", "/environments", { name: env.name })}
        description={env.status_reason ?? undefined}
      >
        <div className="flex flex-wrap items-center gap-3">
          <ShellLink environmentId={env.id} orgId={env.org_id} personal={org?.kind === "personal"} />
          <VpnLink environmentId={env.id} orgId={env.org_id} personal={org?.kind === "personal"} />
          <ActionButton env={env} />
        </div>
      </PageHeader>
      <section className={cn(cardClass, "mb-5 grid grid-cols-2 gap-x-6 gap-y-4 p-5 md:grid-cols-3 xl:grid-cols-6")} aria-label={t.facts}>
        <Field label={t.instance} mono>
          {instanceName(env, org)}
        </Field>
        <Field label={t.baseImage} mono>
          {env.base_image}
        </Field>
        <Field label={t.workerImage} mono>
          <span title={env.worker_image}>{env.worker_image}</span>
        </Field>
        <Field label={t.docker}>{env.docker_socket_path ? t.proxiedSocket : t.notSetUp}</Field>
        <Field label={t.runs}>{workloads.environments.live(live)}</Field>
        <Field label={t.created}>{day(env.created_at)}</Field>
      </section>
      <div className="grid gap-5 xl:grid-cols-12">
        <div className="min-w-0 xl:col-span-5">
          <LimitsCard env={env} org={org} />
        </div>
        <div className="min-w-0 xl:col-span-7">
          <WorkspacesCard env={env} />
        </div>
        <div className="min-w-0 xl:col-span-5">
          <NetworkCard env={env} />
        </div>
        <div className="min-w-0 xl:col-span-7">
          <EgressCard env={env} />
        </div>
      </div>
    </>
  );
}
